import { useCallback, useEffect, useState } from "react";
import "./style.css";
import apiServices from "../../../service";
import { ElementData } from "./helper";
import { useStyles } from "./helper";
import { Toaster, Label, Spinner } from "@fluentui/react-components";
import { setDialogModalOptions } from "../../../redux/modelDialogReducer";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { resetFormData, setFormData } from "../../../redux/reducer";
import { ArrowUndo16Regular, ArrowRedo16Regular } from "@fluentui/react-icons";
import ContentLoader from "../../../components/Loader/ContentLoader";
import ASCButton from "../../../components/Button";
import { useFormik } from "formik";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { useNavigate, useParams } from "react-router-dom";

interface UserDefinedFormProps {
  activeCustomerId?: any;
  mode?: string;
  closePage?: () => void;
  module?: string;
}

const UserDefinedForm = ({
  activeCustomerId,
  mode = "",
  closePage,
}: UserDefinedFormProps) => {
  const { clientId } = useParams();
  const { showToast, toasterId } = useToastMessage();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [userDefinedData, setUserDefinedData] = useState<any[]>([]);
  const [clientDefinedData, setClientDefinedData] = useState<any[]>([]);
  const [selectable, setSelectable] = useState<any[]>([]);
  const [selectFieldOptions, setSelectFieldOptions] = useState<any[]>([]);
  const [selectionListFields, setSelectionListFields] = useState<any[]>([]);
  const [formMode, setFormMode] = useState(mode === "add" ? "New" : "");
  const [options, setOptions] = useState<any>({});
  const [selectionListNewItems, setSelectionListNewItems] = useState<{
    [key: string]: { value: string; label: string };
  }>({});
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();

  const [initialFormValues, setInitialFormValues] = useState<{
    [key: string]: any;
  }>({});
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [history, setHistory] = useState<{ [key: string]: any }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const formik = useFormik<{ [key: string]: any }>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      userDefinedData.forEach((item) => {
        const value = values[item.name] || "";
        if (item.type === 0 && value && value.length > item.length) {
          errors[
            item.name
          ] = `Length of ${item.name} should not be greater than ${item.length}`;
        } else if (
          item.type !== 0 &&
          item.type !== 3 &&
          value &&
          value.toString().length > item.length
        ) {
          errors[
            item.name
          ] = `Value of ${item.name} should not be more than ${item.length} digits`;
        }
      });
      return errors;
    },
    onSubmit: async () => {
      if (Object.keys(formik.errors).length === 0) {
        await onSubmitForm();
      }
    },
  });

  useEffect(() => {
    if (Object.keys(initialFormValues).length > 0) {
      setHistory([initialFormValues]);
      setHistoryIndex(0);
    }
  }, [initialFormValues]);
  const hasChanges = useCallback(() => {
    return JSON.stringify(formValues) !== JSON.stringify(initialFormValues);
  }, [formValues, initialFormValues]);
  useEffect(() => {
    apiServices.systemManager.userDefined
      .getList()
      .then((res) => {
        if (res.data && res.data.data) {
          setUserDefinedData(res.data.data);
          const values = res.data.data.reduce((acc: any, item: any) => {
            acc[item.name] = "";
            return acc;
          }, {});
          setInitialFormValues(values);
          setFormValues(values);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);
  useEffect(() => {
    if (activeCustomerId) {
      getClientDefinedData(activeCustomerId);
    }
  }, [activeCustomerId]);

  const getClientDefinedData = (customer_id: string) => {
    setLoading(true);
    apiServices.client
      .getUserDefined(customer_id)
      .then((response) => {
        if (!response.data.isError) {
          setClientDefinedData(response.data.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    if (clientDefinedData.length > 0 && userDefinedData.length > 0) {
      const newValues = { ...initialFormValues };
      const newSelectItems: {
        [key: string]: { value: string; label: string };
      } = {};

      clientDefinedData.forEach((field) => {
        field.user_defined_value.forEach((value: any) => {
          if (value.customer_id === activeCustomerId) {
            newValues[field.name] = value.value;
            if (field.type === 0) {
              newSelectItems[field.name] = {
                value: value.value,
                label: value.value,
              };
            }
          }
        });
      });

      setInitialFormValues(newValues);
      setFormValues(newValues);
      setSelectionListNewItems(newSelectItems);
      formik.setValues(newValues);
    }
  }, [clientDefinedData, userDefinedData]);

  const addToHistory = useCallback(
    (newState: { [key: string]: any }) => {
      setHistory((prevHistory) => {
        const lastState = prevHistory[prevHistory.length - 1];
        if (JSON.stringify(lastState) === JSON.stringify(newState)) {
          return prevHistory;
        }
        const newHistory = prevHistory.slice(0, historyIndex + 1);
        return [...newHistory, newState];
      });
      setHistoryIndex((prevIndex) => prevIndex + 1);
    },
    [historyIndex]
  );
  const handleOnChange = (field: string, value: any) => {
    const newData = { ...formValues, [field]: value };
    addToHistory(newData);
    setFormValues(newData);
    formik.setFieldValue(field, value, true);
    const isUnchanged =
      JSON.stringify(initialFormValues) === JSON.stringify(newData);
    dispatch(isUnchanged ? resetFormData() : setFormData());
    setFormMode(mode === "add" ? "New" : isUnchanged ? "" : "Changed");
  };

  useEffect(() => {
    getSelectionListOptions();
  }, [userDefinedData]);

  const getSelectionListOptions = async () => {
    try {
      if (userDefinedData.length > 0) {
        const filteredData = userDefinedData.filter(
          (itm: any) => itm.type === 0
        );
        const fieldLbl = filteredData
          .map((itm: any) => `${itm.name}:UserDefinedList`)
          .join(",");
        if (fieldLbl) {
          const response = await apiServices.selectionList.getOptions(fieldLbl);
          if (response.data && response.data.data) {
            setSelectFieldOptions(response.data.data);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching selection list options:", error);
    }
  };

  function handleAdd(element: any) {
    const value = formik.values[element.name];
    const isUpdate = clientDefinedData.some(
      (field: any) => field.user_defined_field_id === element.id
    );
    const reqBody = isUpdate
      ? { user_defined_value: { value } }
      : {
          user_defined_value: {
            value,
            user_defined_field_id: element.id,
            customer_id: activeCustomerId,
          },
        };
    const apiCall = isUpdate
      ? apiServices.client.updateUserDefined(activeCustomerId, reqBody)
      : apiServices.client.addUserDefined(reqBody);

    apiCall
      .then((response: any) => {
        if (!response.data.isError) {
          showToast(response.data.message, "", "success");
        } else {
          showToast("Something went wrong", "", "error");
        }
      })
      .catch((err: any) => {
        console.log(err);
        showToast("Something went wrong", "", "error");
      });
  }

  const newItemAdded = (item: any, field: string, action: string) => {
    if (item !== undefined) {
      setSelectionListNewItems((prevData) => {
        const newData = { ...prevData };
        newData[field] =
          action === "add"
            ? { value: item.value, label: item.value }
            : { value: "", label: "" };
        return newData;
      });
    }
  };

  const newOptionAdded = (element: any, label: string) => {
    setOptions((prev: any) => ({
      ...prev,
      [element.name]: [{ label, value: label }],
    }));
  };

  const addNewOption = (fieldName: string, optionData: any) => {
    setSelectionListFields((prev: any) =>
      prev.map((opt: any) =>
        opt.field === fieldName
          ? { ...opt, option: [...opt.option, optionData] }
          : opt
      )
    );
  };

  const addNewFieldOption = (field: string, newOption: any) => {
    setSelectable((prev: any) => {
      const existingItemIndex = prev.findIndex(
        (item: any) => item.field === field
      );
      if (existingItemIndex !== -1) {
        const updatedData = [...prev];
        updatedData[existingItemIndex] = { field, value: newOption };
        return updatedData;
      }
      return [...prev, { field, value: newOption }];
    });
  };

  const optionSelected = (option: any) => {
    setSelectable(option);
  };

  const getFieldValue = (fieldName: string) => {
    const foundItem = selectable.find((item: any) => item.field === fieldName);
    return foundItem?.value ?? null;
  };

  const undo = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    const prevState = history[newIndex];
    setFormValues(prevState);
    formik.setValues(prevState);
    setHistoryIndex(newIndex);
    const isUnchanged =
      JSON.stringify(initialFormValues) === JSON.stringify(prevState);
    dispatch(isUnchanged ? resetFormData() : setFormData());
    setFormMode(mode === "add" ? "New" : isUnchanged ? "" : "Changed");
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    const nextState = history[newIndex];
    setFormValues(nextState);
    formik.setValues(nextState);
    setHistoryIndex(newIndex);
    const isUnchanged =
      JSON.stringify(initialFormValues) === JSON.stringify(nextState);
    dispatch(isUnchanged ? resetFormData() : setFormData());
    setFormMode(mode === "add" ? "New" : isUnchanged ? "" : "Changed");
  };

  const handleReset = () => {
    setHistory([initialFormValues]);
    setHistoryIndex(0);
    setFormValues(initialFormValues);
    formik.resetForm({ values: initialFormValues });
    dispatch(resetFormData());
    const resetSelectionItems = userDefinedData
      .filter((item) => item.type === 0)
      .reduce((acc: any, item) => {
        acc[item.name] = { value: "", label: "" };
        return acc;
      }, {});
    setSelectionListNewItems(resetSelectionItems);
    setFormMode(mode === "add" ? "New" : "");
  };
  const isFormDataUpdated = useSelector(
    (state: RootState) => state.someReducer.isFormDataUpdated
  );

  const onSubmitForm = async () => {
    if (Object.keys(formik.values).length === 0) return;
    setIsLoading(true);

    const addItemList: any[] = [];
    const updateItemList: any[] = [];

    Object.keys(formik.values).forEach((key) => {
      const matchingItem = clientDefinedData.find(
        (item: any) => item.name === key
      );
      if (matchingItem) {
        updateItemList.push({
          user_defined_value_id:
            matchingItem.user_defined_value[0].user_defined_value_id,
          value: formik.values[key],
          user_defined_field_id: matchingItem.user_defined_field_id,
        });
      } else {
        const idObj = userDefinedData.find((item: any) => item.name === key);
        if (idObj) {
          addItemList.push({
            user_defined_field_id: idObj.id,
            value: formik.values[key],
            customer_id: activeCustomerId,
          });
        }
      }
    });

    try {
      const promises = [];
      if (addItemList.length > 0) {
        promises.push(
          apiServices.client.addUserDefined({
            user_defined_values: addItemList,
          })
        );
      }
      if (updateItemList.length > 0) {
        promises.push(
          apiServices.client.updateUserDefined(activeCustomerId, {
            user_defined_values: updateItemList,
          })
        );
      }
      const responses = await Promise.all(promises);
      responses.forEach((response) => {
        if (!response.data.isError) {
          setTimeout(() => {
            showToast(response.data.message, "", "success");
          }, 2000);
          handleReset();
          getClientDefinedData(activeCustomerId);
        } else {
          showToast("Something went wrong", "", "error");
        }
      });
    } catch (error) {
      console.error(error);
      showToast("Something went wrong", "", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return loading ? (
    <ContentLoader />
  ) : (
    <div>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div className="flex_wraper">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Label size="medium" weight="semibold" className={classes.message}>
            {formMode && (
              <>
                <span>**</span> {formMode} <span>**</span>
              </>
            )}
          </Label>
        </div>
        <ASCButton
          shape="rounded"
          icon={<ArrowUndo16Regular />}
          onClick={undo}
          disabled={historyIndex <= 0 && !hasChanges()}
        />
        <ASCButton
          shape="rounded"
          icon={<ArrowRedo16Regular />}
          onClick={redo}
          disabled={historyIndex >= history.length - 1}
        />
        <ASCButton
          shape="rounded"
          appearance="transparent"
          onClick={handleReset}
          disabled={!isFormDataUpdated}
          label="Reset All"
        />
      </div>
      <div className="user_defined_page">
        <div className="user_defined">
          {userDefinedData?.map((item: any) => (
            <div key={item.id}>
              {ElementData({
                element: item,
                handleAdd,
                formik,
                selectable,
                setSelectable,
                options,
                newOptionAdded,
                optionSelected,
                addNewOption,
                selectionListFields,
                addNewFieldOption,
                getFieldValue,
                clientDefinedData,
                selectFieldOptions,
                selectionListNewItems,
                handleOnChange,
                newItemAdded,
                setIsFormError: () => {},
              })}
            </div>
          ))}
        </div>
        <div className="button_wrapper">
          <ASCButton
            shape="rounded"
            className="asc-button-primary"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            disabled={
              Object.keys(formik.errors).length > 0 || !isFormDataUpdated
            }
            icon={isLoading ? <Spinner size="tiny" /> : null}
            label={isLoading ? "Processing..." : "Save"}
          />
          <ASCButton
            disabled={!isFormDataUpdated}
            onClick={() => {
              if (userDefinedData.length > 0) {
                dispatch(
                  setDialogModalOptions({
                    open: true,
                    content_line_1:
                      "We have detected an attempt to move away from the current page.",
                    content_line_2:
                      "This would cause all changes made to be lost",
                    cancel: {
                      onclick: () => {
                        dispatch(setDialogModalOptions({ open: false }));
                        dispatch(resetFormData());
                        handleReset();
                        navigate(`/clients/${clientId}/user-defined`);
                      },
                      label: "Leave the Page",
                    },
                    no: {
                      onclick: () =>
                        dispatch(setDialogModalOptions({ open: false })),
                      label: "Stay on Page",
                      type: "primary",
                    },
                  })
                );
              } else {
                navigate(`/clients/${clientId}/user-defined`);
              }
            }}
            label="Cancel"
          />
        </div>
      </div>
    </div>
  );
};

export default UserDefinedForm;
